import { Card, CardContent } from '../../components/ui/card';
import { useFormatters } from '../../contexts/PreferencesContext';
import { ArrowUpRight, ArrowDownRight, Equal, HelpCircle, type LucideIcon } from 'lucide-react';
import type { PriceVarianceTotals } from '../../lib/api/priceVariance';

interface Tile {
  label: string;
  value: string;
  sub?: string;
  icon: LucideIcon;
  iconClass: string;
  cardClass?: string;
}

/** Over / on / under contract, and what could not be compared, for the period. */
export default function PriceVarianceTiles({ totals, loading }: { totals: PriceVarianceTotals | null; loading: boolean }) {
  const { formatCurrency } = useFormatters();
  const money = (v: string) => formatCurrency(Number(v), 'GBP', 0);
  const lines = (n: number) => `${n.toLocaleString()} line${n === 1 ? '' : 's'}`;
  const tiles: Tile[] = [
    {
      label: 'Over contract rate',
      value: totals ? money(totals.over_value) : '—',
      sub: totals ? `${lines(totals.over_count)} · ${totals.suppliers_over} supplier${totals.suppliers_over === 1 ? '' : 's'}` : undefined,
      icon: ArrowUpRight, iconClass: 'text-red-500',
      cardClass: totals && totals.over_count > 0 ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30' : undefined,
    },
    {
      label: 'On contract rate',
      value: totals ? lines(totals.exact_count) : '—',
      sub: 'Invoiced exactly at the contracted price',
      icon: Equal, iconClass: 'text-green-500',
    },
    {
      label: 'Under contract rate',
      value: totals ? money(totals.under_value) : '—',
      sub: totals ? `${lines(totals.under_count)} below the rate` : undefined,
      icon: ArrowDownRight, iconClass: 'text-amber-500',
    },
    {
      label: 'No contract rate',
      value: totals ? lines(totals.uncompared_count) : '—',
      sub: totals ? `${money(totals.uncompared_value)} not comparable` : undefined,
      icon: HelpCircle, iconClass: 'text-muted-foreground',
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="price-variance-tiles">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <Card key={tile.label} className={tile.cardClass}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{tile.label}</p>
                  {loading ? <div className="mt-1 h-8 w-24 animate-pulse rounded bg-muted" /> : <p className="text-2xl font-bold">{tile.value}</p>}
                  {tile.sub && !loading && <p className="text-xs text-muted-foreground">{tile.sub}</p>}
                </div>
                <Icon className={`h-8 w-8 ${tile.iconClass}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
